import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Zap, AlertCircle, TrendingUp, CheckCircle2 } from "lucide-react";

export default function DelinquencyWaterfallPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/delinquency/waterfall?property_id=${propertyId}`);
      setItems(data.items || []);
      setStats(data.stats || null);
    } catch (e) { toast.error("Failed to load"); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [propertyId]);

  const backfill = async () => {
    try {
      const { data } = await api.post("/admin/delinquency/waterfall/backfill");
      toast.success(`Backfill scanned=${data.scanned} created=${data.created}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const advance = async (w) => {
    const notes = window.prompt("Admin notes (optional):");
    if (notes === null) return;
    try {
      const { data } = await api.post(`/admin/delinquency/waterfall/${w.id}/advance`, { admin_notes: notes });
      toast.success(`Stage fired: ${data.stage_fired}`);
      setDetail(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const resolve = async (w) => {
    if (!window.confirm(`Mark workflow for unit ${w.unit} as resolved?`)) return;
    try {
      await api.post(`/admin/delinquency/waterfall/${w.id}/resolve`);
      toast.success("Resolved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="waterfall-page">
      <PageHeader
        eyebrow="Financials"
        title="Delinquency Waterfall"
        subtitle="Formal 5-stage escalation: 7d gentle → 14d late notice + fee → 30d intent-to-lien → 45d attorney handoff → 60d small claims."
        actions={
          <Button variant="outline" onClick={backfill} data-testid="backfill-btn">Backfill existing charges</Button>
        }
      />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Active Workflows</div>
            <div className="text-2xl font-semibold mt-1" data-testid="stat-active">{stats.active}</div>
          </Card>
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Past-Due Balance</div>
            <div className="text-2xl font-semibold mt-1 text-rose-400" data-testid="stat-past-due">${(stats.past_due_amount || 0).toLocaleString()}</div>
          </Card>
          <Card className={`p-4 border-white/10 bg-card ${stats["60d_plus"] > 0 ? "border-rose-500/40" : ""}`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <AlertCircle size={12} /> 60d+ Delinquent
            </div>
            <div className={`text-2xl font-semibold mt-1 ${stats["60d_plus"] > 0 ? "text-rose-400" : ""}`}>{stats["60d_plus"]}</div>
          </Card>
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Tracked</div>
            <div className="text-2xl font-semibold mt-1">{stats.total}</div>
          </Card>
        </div>
      )}

      <Card className="border-white/10 bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Unit</TableHead>
              <TableHead>Resident</TableHead>
              <TableHead>Period</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Days Past</TableHead>
              <TableHead>Current Stage</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow className="border-white/5"><TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm" data-testid="waterfall-empty">
                No delinquent accounts. Workflows are auto-created after rent is 7+ days past due.
              </TableCell></TableRow>
            )}
            {items.map((w) => (
              <TableRow key={w.id} className="border-white/5" data-testid={`workflow-${w.id}`}>
                <TableCell className="font-medium">{w.unit || "—"}</TableCell>
                <TableCell className="text-sm">{w.resident_name}</TableCell>
                <TableCell className="text-sm">{w.period}</TableCell>
                <TableCell className="text-right font-mono text-rose-400">${(w.current_balance || 0).toLocaleString()}</TableCell>
                <TableCell><Badge variant={w.days_past_due >= 60 ? "destructive" : w.days_past_due >= 30 ? "secondary" : "outline"}>{w.days_past_due}d</Badge></TableCell>
                <TableCell className="text-sm">
                  {w.current_stage ? (
                    <span className="flex items-center gap-1">
                      <Zap size={12} className="text-amber-500" /> {w.current_stage.title}
                    </span>
                  ) : <span className="text-muted-foreground">complete</span>}
                </TableCell>
                <TableCell><Badge variant={w.status === "active" ? "default" : "secondary"}>{w.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => setDetail(w)} data-testid={`open-workflow-${w.id}`}>Open</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="workflow-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>Waterfall · Unit {detail.unit} · {detail.resident_name} · {detail.period}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  First due: {detail.first_due_date} · Days past: <strong>{detail.days_past_due}</strong> · Balance <strong className="text-rose-400">${(detail.current_balance || 0).toLocaleString()}</strong>
                </div>
                <div className="space-y-2">
                  {detail.stages.map((s, i) => (
                    <Card key={s.key} className={`p-3 border-white/10 ${s.completed ? "bg-emerald-500/5 border-emerald-500/30" : i === detail.current_stage_index ? "bg-amber-500/5 border-amber-500/40" : "bg-card"}`} data-testid={`stage-${s.key}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">
                            {s.completed ? <CheckCircle2 size={12} className="inline mr-1 text-emerald-500" /> : null}
                            Day {s.day}: {s.title}
                          </div>
                          {s.sent_at && <div className="text-[10px] text-muted-foreground">Sent {s.sent_at.slice(0, 16)} by {s.sent_by === "system_cron" ? "cron" : "admin"}</div>}
                        </div>
                        {!s.completed && i === detail.current_stage_index && detail.status === "active" && (
                          <Button size="sm" onClick={() => advance(detail)} data-testid={`fire-stage-${s.key}`}>Fire now</Button>
                        )}
                      </div>
                      {s.rendered_body && (
                        <div className="mt-2 pt-2 border-t border-white/10 text-xs whitespace-pre-line text-muted-foreground max-h-24 overflow-y-auto">
                          {s.rendered_body}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
                {detail.status === "active" && (
                  <Button variant="default" onClick={() => resolve(detail)} className="gap-2 bg-emerald-600" data-testid="resolve-workflow">
                    <CheckCircle2 size={14} /> Mark resolved
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
